import React, { useState } from 'react';
import { Box, Typography, Chip, Button } from '@mui/material';
import { PlayArrow, AccessTime, BarChart, ArrowBack, Info } from '@mui/icons-material';
import { Lecon } from '../../types';
import VideoPlayer from '../common/VideoPlayer';

interface LeconMobileViewProps {
  lecon: Omit<Lecon, 'id'>;
  chapitreTitre?: string;
}

/**
 * Composant qui reproduit l'affichage mobile d'une leçon
 * Basé sur le design de l'application mobile Suklu Academy
 */
const LeconMobileView: React.FC<LeconMobileViewProps> = ({ lecon, chapitreTitre }) => {
  // Calculer le nombre de vidéos (simulé pour le preview)
  const nombreVideos = lecon.videoId ? 4 : 0;
  const progression = 50; // Progression simulée

  // États pour gérer l'interface
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'videos' | 'resumes'>('videos');

  return (
    <Box
      sx={{
        height: '667px', // Hauteur iPhone SE / 8 (standard)
        width: '100%',
        maxWidth: '375px', // Largeur iPhone SE / 8
        bgcolor: '#f5f5f5',
        overflow: 'auto', // Scroll interne
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #ddd',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      }}
    >
      {/* Header avec titre de la leçon */}
      <Box
        sx={{
          bgcolor: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <ArrowBack sx={{ fontSize: 20, color: '#666' }} />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: '16px',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {lecon.titre || 'Titre de la leçon'}
        </Typography>
        <Info sx={{ fontSize: 20, color: '#4caf50' }} />
      </Box>

      {/* Zone vidéo */}
      <VideoPlayer
        videoId={lecon.videoId}
        aspectRatio="16/9"
        title={lecon.titre || 'Vidéo de la leçon'}
      />

      {/* Carte d'information principale */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: '12px',
            p: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          {/* Titre et matière */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: '18px',
                fontWeight: 700,
                mb: 1,
                color: '#1a1a1a',
              }}
            >
              {chapitreTitre ? `${chapitreTitre.split(':')[0]} : ${lecon.titre}` : lecon.titre}
            </Typography>

            {/* Badges */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip
                label="Math"
                size="small"
                sx={{
                  bgcolor: '#4caf50',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '12px',
                  height: '24px',
                }}
              />
              <Chip
                label={`${nombreVideos} vues`}
                size="small"
                sx={{
                  bgcolor: '#ff9800',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '12px',
                  height: '24px',
                }}
              />
            </Box>

            {/* Indicateurs */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PlayArrow sx={{ fontSize: 16, color: '#666' }} />
                <Typography variant="caption" sx={{ color: '#666', fontSize: '13px' }}>
                  {nombreVideos} vidéos
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTime sx={{ fontSize: 16, color: '#666' }} />
                <Typography variant="caption" sx={{ color: '#666', fontSize: '13px' }}>
                  +{lecon.tempsEstimeMinutes || 0} pts
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BarChart sx={{ fontSize: 16, color: '#666' }} />
                <Typography variant="caption" sx={{ color: '#666', fontSize: '13px' }}>
                  {lecon.niveauDifficulte || 'moyen'}
                </Typography>
              </Box>
            </Box>

            {/* Description */}
            <Typography
              variant="body2"
              sx={{
                fontSize: '14px',
                color: '#666',
                lineHeight: 1.6,
                mb: 1,
              }}
            >
              {lecon.description
                ? descriptionExpanded
                  ? lecon.description
                  : lecon.description.length > 150
                  ? `${lecon.description.substring(0, 150)}...`
                  : lecon.description
                : descriptionExpanded
                ? 'La leçon sur les angles inscrits vise à comprendre la relation géométrique entre un angle dont le sommet est sur un cercle et l\'arc intercepté par cet angle. Elle permet aux élèves de découvrir et de démontrer deux propriétés essentielles : la mesure d\'un angle inscrit est égale à la moitié de la mesure de l\'angle au centre associé, et deux angles inscrits interceptant le même arc ont la même mesure. Ces notions sont fondamentales pour développer le raisonnement géométrique et la capacité de démonstration des élèves de troisième.'
                : 'La leçon sur les angles inscrits vise à comprendre la relation géométrique entre un angle dont le sommet est sur un cercle et l\'arc intercepté par cet angle. Elle permet aux élèves de découv...'}
            </Typography>
            {((lecon.description && lecon.description.length > 150) || !lecon.description) && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  color: '#2196f3',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                onClick={() => setDescriptionExpanded(!descriptionExpanded)}
              >
                {descriptionExpanded ? 'Voir moins' : 'Voir plus'}
              </Typography>
            )}
          </Box>

          {/* Barre de progression */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '12px', color: '#666' }}>
                Progression
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '14px',
                  fontWeight: 700,
                  bgcolor: '#e8f5e9',
                  color: '#2e7d32',
                  px: 1,
                  py: 0.5,
                  borderRadius: '8px',
                }}
              >
                {progression}%
              </Typography>
            </Box>
            <Box
              sx={{
                height: '8px',
                bgcolor: '#e0e0e0',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${progression}%`,
                  height: '100%',
                  bgcolor: '#4caf50',
                  borderRadius: '4px',
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Onglets */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          px: 2,
          mb: 2,
        }}
      >
        <Button
          variant={activeTab === 'videos' ? 'contained' : 'outlined'}
          onClick={() => setActiveTab('videos')}
          sx={{
            flex: 1,
            bgcolor: activeTab === 'videos' ? '#4caf50' : 'transparent',
            color: activeTab === 'videos' ? 'white' : '#4caf50',
            borderColor: '#4caf50',
            borderRadius: '20px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            py: 1,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: activeTab === 'videos' ? '#45a049' : 'rgba(76, 175, 80, 0.04)',
              borderColor: '#45a049',
            },
          }}
        >
          Vidéos
        </Button>
        <Button
          variant={activeTab === 'resumes' ? 'contained' : 'outlined'}
          onClick={() => setActiveTab('resumes')}
          sx={{
            flex: 1,
            bgcolor: activeTab === 'resumes' ? '#4caf50' : 'transparent',
            color: activeTab === 'resumes' ? 'white' : '#4caf50',
            borderColor: '#4caf50',
            borderRadius: '20px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            py: 1,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: activeTab === 'resumes' ? '#45a049' : 'rgba(76, 175, 80, 0.04)',
              borderColor: '#45a049',
            },
          }}
        >
          Résumés
        </Button>
        <Box
          sx={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            bgcolor: '#4caf50',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '3px solid white',
            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
          }}
        >
          <Box
            sx={{
              width: '24px',
              height: '24px',
              bgcolor: 'white',
              borderRadius: '50%',
            }}
          />
        </Box>
      </Box>

      {/* Contenu selon l'onglet actif */}
      <Box sx={{ px: 2, pb: 3 }}>
        {activeTab === 'videos' ? (
          // Liste des sections/vidéos
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: '12px',
              overflow: 'hidden',
              border: '2px solid #4caf50',
            }}
          >
            {/* Section 1 - Actuelle */}
            <Box
              sx={{
                p: 2,
                bgcolor: '#e8f5e9',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  bgcolor: '#4caf50',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '14px',
                }}
              >
                1
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#2e7d32',
                    mb: 0.5,
                  }}
                >
                  {lecon.titre}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '12px',
                    color: '#666',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {lecon.description
                    ? lecon.description.substring(0, 50) + '...'
                    : 'La leçon sur les angles inscrits vise à comprendre la relation géométrique entre un an...'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccessTime sx={{ fontSize: 14, color: '#666' }} />
                    <Typography variant="caption" sx={{ fontSize: '12px', color: '#666' }}>
                      {lecon.tempsEstimeMinutes || 90} min
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BarChart sx={{ fontSize: 14, color: '#666' }} />
                    <Typography variant="caption" sx={{ fontSize: '12px', color: '#666' }}>
                      {lecon.niveauDifficulte || 'moyen'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Chip
                label="Actuelle"
                size="small"
                sx={{
                  bgcolor: '#4caf50',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '11px',
                  height: '22px',
                }}
              />
            </Box>

            {/* Section 2 */}
            <Box
              sx={{
                p: 2,
                bgcolor: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <Box
                sx={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  bgcolor: '#e0e0e0',
                  color: '#666',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '14px',
                }}
              >
                2
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#1a1a1a',
                    mb: 0.5,
                  }}
                >
                  Propriétés des angles inscrits
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '12px',
                    color: '#999',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  La leçon sur les angles inscrits vise à développer la compréhension des relations géométriques entre les an...
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccessTime sx={{ fontSize: 14, color: '#999' }} />
                    <Typography variant="caption" sx={{ fontSize: '12px', color: '#999' }}>
                      90 min
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BarChart sx={{ fontSize: 14, color: '#999' }} />
                    <Typography variant="caption" sx={{ fontSize: '12px', color: '#999' }}>
                      moyen
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ fontSize: '20px', color: '#ccc' }}>›</Box>
            </Box>
          </Box>
        ) : (
          // Onglet Résumés - Afficher le contenu LaTeX
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontSize: '18px',
                fontWeight: 700,
                mb: 2,
                px: 2,
                color: '#1a1a1a',
              }}
            >
              Résumé du cours
            </Typography>
            <Box
              sx={{
                bgcolor: 'white',
                borderRadius: '12px',
                p: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              {lecon.contenuLatex ? (
                <Typography
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    color: '#333',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.5,
                    m: 0,
                  }}
                >
                  {lecon.contenuLatex}
                </Typography>
              ) : (
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#999',
                    textAlign: 'center',
                    py: 4,
                  }}
                >
                  Aucun contenu LaTeX disponible pour cette leçon.
                  <br />
                  Utilisez le bouton "Générer avec IA" dans le formulaire pour créer le résumé.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LeconMobileView;
